/**
 * /accounting/ar/:customerId — Customer statement & collections workspace.
 *
 * Per-customer invoice list, payment history, dunning recommendation,
 * an as-of-date selector, a write-off dialog, and a copy-ready statement.
 */
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Copy, FileMinus, Download } from "lucide-react";
import { CockpitPage, CockpitSection } from "@/components/cockpit/CockpitShell";
import { BooksNavRail } from "@/components/accounting/BooksNavRail";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { useGeneralLedger } from "@/data/accounting/GeneralLedgerContext";
import { useSeedGeneralLedger } from "@/data/accounting/useSeedGeneralLedger";
import { getCustomerAR, recommendDunning } from "@/data/accounting/ar/arAging";
import { buildWriteOffJournal } from "@/data/accounting/ar/arWriteOff";
import { exportCsv } from "@/lib/csvExport";
import { toast } from "sonner";

const fmt = (n: number) =>
  `${n < 0 ? "-" : ""}$${Math.abs(Math.round(n)).toLocaleString()}`;
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function ARCustomerStatementPage() {
  useSeedGeneralLedger();
  const { customerId } = useParams<{ customerId: string }>();
  const { journalEntries, postJournal } = useGeneralLedger();
  const [asOf, setAsOf] = useState(todayIso());
  const [writeOffOpen, setWriteOffOpen] = useState(false);

  const decoded = decodeURIComponent(customerId ?? "");
  const ar = useMemo(
    () => (decoded ? getCustomerAR(journalEntries, decoded, asOf) : null),
    [journalEntries, decoded, asOf],
  );

  // All AR activity (debit invoices + credit payments) for history, scoped
  // to the as-of date so the statement is a true point-in-time view.
  const history = useMemo(() => {
    const rows: Array<{ date: string; ref: string; type: "Invoice" | "Payment"; amount: number; jeNumber: string }> = [];
    for (const je of journalEntries) {
      if (je.status !== "posted") continue;
      if (je.postingDate > asOf) continue;
      for (const l of je.lines) {
        if (l.accountId !== "1100" || l.customerId !== decoded) continue;
        rows.push({
          date: je.postingDate,
          ref: l.memo ?? je.number,
          type: l.side === "debit" ? "Invoice" : "Payment",
          amount: l.amount,
          jeNumber: je.number,
        });
      }
    }
    return rows.sort((a, b) => b.date.localeCompare(a.date));
  }, [journalEntries, decoded, asOf]);

  if (!ar) {
    return (
      <CockpitPage>
        <BooksNavRail />
        <Link to="/accounting/ar" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="h-3 w-3" /> Back to AR Collections
        </Link>
        <div className="rounded-md border border-border bg-card p-6 text-sm text-muted-foreground">
          No AR activity found for this customer.
        </div>
      </CockpitPage>
    );
  }

  const dunning = recommendDunning(ar);

  const copyStatement = () => {
    const lines = [
      `STATEMENT — ${ar.customerLabel}`,
      `As of ${asOf}`,
      ``,
      `Total open: ${fmt(ar.totalOpen)}`,
      ...(ar.unappliedCredit > 0 ? [`Unapplied credit: ${fmt(ar.unappliedCredit)}`] : []),
      ``,
      `Open invoices:`,
      ...ar.openInvoices.map(inv =>
        `  ${inv.postingDate}  ${inv.invoiceRef.padEnd(20)}  ${fmt(inv.openAmount).padStart(10)}  (${inv.daysOpen}d)`,
      ),
      ``,
      `Recommended next action: ${dunning.label} — ${dunning.rationale}`,
    ].join("\n");
    navigator.clipboard.writeText(lines);
    toast.success("Statement copied to clipboard");
  };

  const downloadCsv = () => {
    exportCsv(
      `statement-${decoded}-${asOf}.csv`,
      ["Date", "Type", "JE#", "Reference", "Amount"],
      history.map(r => [r.date, r.type, r.jeNumber, r.ref, r.amount.toFixed(2)]),
    );
  };

  const handleWriteOff = (amount: number, reason: string) => {
    try {
      const je = postJournal(buildWriteOffJournal({
        customerId: decoded,
        amount,
        postingDate: todayIso(),
        reason,
        createdBy: "Steve",
      }));
      toast.success(`Wrote off ${fmt(amount)} — ${je.number}`);
      setWriteOffOpen(false);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <CockpitPage>
      <BooksNavRail />
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link to="/accounting/ar" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="h-3 w-3" /> Back to AR Collections
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight">{ar.customerLabel}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Customer statement · last activity {ar.lastActivityDate ?? "—"}
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">As of</Label>
            <Input type="date" className="h-8 w-40" value={asOf} onChange={e => setAsOf(e.target.value)} />
          </div>
          <Button variant="outline" size="sm" onClick={downloadCsv} disabled={history.length === 0}>
            <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={copyStatement}>
            <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
          </Button>
          <Button variant="outline" size="sm" onClick={() => setWriteOffOpen(true)} disabled={ar.totalOpen <= 0}>
            <FileMinus className="h-3.5 w-3.5 mr-1.5" /> Write off
          </Button>
        </div>
      </div>

      <CockpitSection>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi label="Open balance" value={fmt(ar.totalOpen)} tone={ar.totalOpen > 0 ? "primary" : undefined} />
          <Kpi label="Open invoices" value={String(ar.openInvoices.length)} />
          <Kpi label="Oldest" value={`${ar.oldestDaysOpen} days`} tone={ar.oldestDaysOpen > 60 ? "neg" : ar.oldestDaysOpen > 30 ? "warn" : undefined} />
          <Kpi label="Next action" value={dunning.label} tone={dunning.tone === "critical" ? "neg" : dunning.tone === "warn" ? "warn" : undefined} />
          <Kpi label="Unapplied credit" value={fmt(ar.unappliedCredit)} tone={ar.unappliedCredit > 0 ? "primary" : undefined} />
        </div>
      </CockpitSection>

      <CockpitSection>
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Open invoices</div>
        <div className="rounded-md border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Invoice</th>
                <th className="text-left px-3 py-2">Job / Trade</th>
                <th className="text-right px-3 py-2">Original</th>
                <th className="text-right px-3 py-2">Applied</th>
                <th className="text-right px-3 py-2">Open</th>
                <th className="text-right px-3 py-2">Age</th>
              </tr>
            </thead>
            <tbody>
              {ar.openInvoices.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-sm text-muted-foreground">
                    Paid in full.
                  </td>
                </tr>
              )}
              {ar.openInvoices.map(inv => (
                <tr key={inv.jeId} className="border-t border-border">
                  <td className="px-3 py-2 text-muted-foreground">{inv.postingDate}</td>
                  <td className="px-3 py-2 font-medium">{inv.invoiceRef}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {inv.jobId ?? "—"}{inv.tradeType ? ` · ${inv.tradeType}` : ""}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmt(inv.originalAmount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{fmt(inv.appliedAmount)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmt(inv.openAmount)}</td>
                  <td className={`px-3 py-2 text-right text-xs ${inv.daysOpen > 60 ? "text-destructive" : inv.daysOpen > 30 ? "text-amber-600" : "text-muted-foreground"}`}>
                    {inv.daysOpen}d
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CockpitSection>

      <CockpitSection>
        <div className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Activity history</div>
        <div className="rounded-md border border-border bg-card divide-y divide-border">
          {history.length === 0 && (
            <div className="px-3 py-6 text-sm text-muted-foreground text-center">No activity.</div>
          )}
          {history.map((r, i) => (
            <div key={i} className="px-3 py-2 flex items-center justify-between text-sm">
              <div className="flex items-center gap-3">
                <span className="text-xs text-muted-foreground tabular-nums w-24">{r.date}</span>
                <span className={
                  "text-xs px-1.5 py-0.5 rounded-sm " +
                  (r.type === "Invoice" ? "bg-muted" : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400")
                }>
                  {r.type}
                </span>
                <span className="font-medium">{r.ref}</span>
                <span className="text-xs text-muted-foreground">{r.jeNumber}</span>
              </div>
              <span className={`tabular-nums font-medium ${r.type === "Payment" ? "text-emerald-700 dark:text-emerald-400" : ""}`}>
                {r.type === "Payment" ? "-" : ""}{fmt(r.amount)}
              </span>
            </div>
          ))}
        </div>
      </CockpitSection>

      <WriteOffDialog
        open={writeOffOpen}
        onClose={() => setWriteOffOpen(false)}
        customerLabel={ar.customerLabel}
        maxAmount={ar.totalOpen}
        onConfirm={handleWriteOff}
      />
    </CockpitPage>
  );
}

function WriteOffDialog({
  open, onClose, customerLabel, maxAmount, onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  customerLabel: string;
  maxAmount: number;
  onConfirm: (amount: number, reason: string) => void;
}) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const amt = parseFloat(amount) || 0;
  const exceeds = amt > maxAmount + 0.005;
  const reasonOk = reason.trim().length >= 10;
  const canSubmit = amt > 0 && !exceeds && reasonOk;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setAmount(""); setReason(""); } }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Write off — {customerLabel}</DialogTitle>
          <DialogDescription>
            Posts <code>DR 6900 Bad Debt / CR 1100 AR</code> dated today. Reduces the customer's open balance and books the loss to operating expense. Requires a written reason for the audit trail.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Amount</Label>
              <Input
                type="number" min="0" step="0.01" value={amount}
                onChange={e => setAmount(e.target.value)}
                placeholder={`Max ${fmt(maxAmount)}`}
                className="h-8"
              />
              {exceeds && <div className="text-[11px] text-destructive mt-1">Exceeds open balance.</div>}
            </div>
            <div>
              <Label className="text-xs">Open balance</Label>
              <div className="h-8 flex items-center text-sm tabular-nums">{fmt(maxAmount)}</div>
            </div>
          </div>
          <div>
            <Label className="text-xs">Reason (≥10 chars)</Label>
            <Textarea
              rows={3}
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="e.g. Customer bankruptcy filed 2026-03; collection efforts exhausted."
            />
            <div className={`text-[11px] mt-1 ${reasonOk ? "text-muted-foreground" : "text-destructive"}`}>
              {reason.trim().length}/10 minimum
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            variant="destructive"
            disabled={!canSubmit}
            onClick={() => onConfirm(amt, reason.trim())}
          >
            Post write-off
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Kpi({ label, value, tone }: { label: string; value: string; tone?: "primary" | "warn" | "neg" }) {
  const cls =
    tone === "neg" ? "text-destructive" :
    tone === "warn" ? "text-amber-600 dark:text-amber-500" :
    tone === "primary" ? "text-primary" : "";
  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
